import { useToast } from "@design";
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraftActions } from "./useDraftActions";
import { type DraftStepDef, useMultiStepCreate } from "./useMultiStepCreate";

interface Finish {
  /** Error text when a step fails without a server message. */
  fallback: string;
  success: string;
  /** The created or saved entity, known once the steps ran. */
  entityUuid: () => string | null;
  /** What to refetch for it (its list, its detail page). */
  invalidate: (uuid: string) => QueryKey[];
}

/** Runs a card's steps; once they all succeed, marks the card done, records it on the draft and refreshes what changed. */
export function useDraftRun(draftId: string, steps: readonly DraftStepDef[], finish: Finish) {
  const { t } = useTranslation("buildChat", { keyPrefix: "draft" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const actions = useDraftActions();
  const multi = useMultiStepCreate(steps, finish.fallback);
  const [doneUuid, setDoneUuid] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  // Set before anything is awaited: a click while the card records and refreshes can't run the steps again.
  const done = useRef(false);

  // So the card still says Created / Updated after a reload; without it the card would offer itself again then.
  const record = async (uuid: string) => {
    try {
      await actions.markApplied(draftId, uuid);
    } catch {
      await actions.markApplied(draftId, uuid).catch(() => toast.error(t("recordFailed")));
    }
  };

  const after = async (ok: boolean) => {
    const uuid = finish.entityUuid();
    if (!ok || !uuid) return;
    done.current = true;
    setDoneUuid(uuid);
    setFinishing(true);
    toast.success(finish.success);
    try {
      await record(uuid);
      await Promise.all([
        ...finish.invalidate(uuid).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        actions.refreshChat(),
      ]);
    } finally {
      setFinishing(false);
    }
  };
  const start = (go: () => Promise<boolean>) => {
    if (!done.current) void go().then(after);
  };

  return {
    steps: multi.steps,
    busy: multi.busy || finishing,
    doneUuid,
    run: () => start(multi.run),
    retry: () => start(multi.retry),
  };
}
